import {
  Directive,
  Input,
  AfterViewInit,
  OnChanges,
  ElementRef,
  Renderer2,
  AfterViewChecked,
  SimpleChanges,
  RendererStyleFlags2,
  OnDestroy,
  Optional,
} from '@angular/core';
import { Calendar } from 'primeng/calendar';
import { Subscription } from 'rxjs';

export interface DateStyle {
  style: string;
  value: any;
  flags?: RendererStyleFlags2;
}

export interface DateHighlight {
  date: string;
  styles?: DateStyle[];
  classes?: string[];
}

@Directive({
  selector: '[appHighlightDates]',
  standalone: true,
})
export class HighlightDatesDirective implements AfterViewInit, OnChanges, AfterViewChecked, OnDestroy {

  @Input() appHighlightDates: DateHighlight[] = [];

  private needsApply = false;

  private mutationObserver: MutationObserver | null = null;
  private sub: Subscription | null = null;
  private timeoutIds: ReturnType<typeof setTimeout>[] = [];

  // Tracks applied styles/classes per element so we can clean them up on the next apply
  private appliedHighlights = new Map<HTMLElement, { styles: Set<string>; classes: Set<string> }>();

  constructor(
    private el: ElementRef, 
    private renderer: Renderer2,
    @Optional() private pCalendar: Calendar
  ) {}

  private queueApply(attachOverlayObserver = false): void {
    const id = setTimeout(() => {
      this.needsApply = true;
      this.applyHighlight();
      if (attachOverlayObserver) {
        this.attachObserverToOverlay();
      }
      this.timeoutIds = this.timeoutIds.filter((t) => t !== id);
    }, 50);
    this.timeoutIds.push(id);
  }

  ngAfterViewInit() {
    // Watch for popup open
    if (this.pCalendar) {
        this.sub = this.pCalendar.onShow.subscribe(() => {
            this.queueApply(true);
        });

        this.sub.add(this.pCalendar.onMonthChange.subscribe(() => {
            this.queueApply();
        }));
    }

    // PrimeNG reconstructs the calendar DOM; watch for that and re-apply styles.
    // For inline calendars, el.nativeElement is sufficient.
    this.attachObserver(this.el.nativeElement);
  }

  private attachObserver(target: any) {
    if (typeof MutationObserver !== 'undefined' && target) {
      this.mutationObserver?.disconnect(); // Disconnect previous if any
      this.mutationObserver = new MutationObserver(() => {
        this.needsApply = true;
      });
      this.mutationObserver.observe(target, { childList: true, subtree: true });
    }
  }
  
  private attachObserverToOverlay() {
      const overlay = this.getOverlay();
      if (overlay) {
          // We need a separate observer or reuse? Reuse is tricky if we disconnect.
          // Just run highlighting when needed.
          // Actually, if month changes in popup, MutationObserver on overlay should catch it.
          // But applyHighlight needs to know where to look.
          // Let's just rely on needsApply logic which checks getOverlay() every time.
      }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['appHighlightDates']) {
      // mark that we need to re-apply styles after the view has updated
      this.needsApply = true;
    }
  }

  ngAfterViewChecked(): void {
    if (!this.needsApply) return;
    this.needsApply = false;
    this.applyHighlight();
  }

  ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    this.sub?.unsubscribe();
    for (const id of this.timeoutIds) {
      clearTimeout(id);
    }
    this.timeoutIds = [];
    this.clearAppliedHighlights();
  }

  private clearAppliedHighlights(): void {
    for (const [el, { styles, classes }] of this.appliedHighlights) {
      for (const style of styles) {
        this.renderer.removeStyle(el, style);
      }
      for (const cls of classes) {
        this.renderer.removeClass(el, cls);
      }
    }
    this.appliedHighlights.clear();
  }
  
  private getOverlay(): HTMLElement | null {
      if (this.pCalendar && !this.pCalendar.inline && this.pCalendar.overlayVisible) {
          const cal = this.pCalendar as any;
          return cal.overlay || cal.container || cal.el.nativeElement.querySelector('.p-datepicker');
      }
      return null;
  }

  private applyHighlight() {

    // Requirement: Clear previously applied styles before re-applying to avoid stale highlights
    this.clearAppliedHighlights();

    let root: HTMLElement = this.el.nativeElement;
    
    // For popup calendars, find the overlay
    const overlay = this.getOverlay();
    if (overlay) {
        root = overlay;
    }

    for (const { date, styles = [], classes = [] } of this.appHighlightDates) {

      const parts = date.split('-');
      const year = parts[0];
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);

      const selector = `[data-date="${year}-${month}-${day}"]`;

      const span: HTMLElement = root.querySelector(selector);

      if (!span) { continue; }

      const applied = { styles: new Set<string>(), classes: new Set<string>() };

      for (const { style, value, flags } of styles) {
        this.renderer.setStyle(span, style, value, flags);
        applied.styles.add(style);
      }

      for (const cls of classes) {
        this.renderer.addClass(span, cls);
        applied.classes.add(cls);
      }

      this.appliedHighlights.set(span, applied);
    }
  }
}
